import { ChevronLeftIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-left'
import { MenuRow } from '@repo/studio-components-menu-row'
import { useStudioConfig, useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { useStudioMatchPath, useStudioPathname } from '@repo/studio-config/studio-config-router'
import type {
  ContextualNavigationConfig,
  ContextualNavigationItem,
} from '@repo/studio-config/studio-config-context'
import { Tooltip } from 'primitives/tooltip'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { NavigationItem } from './navigation-item'

export function useActiveContextualNavigation(): ContextualNavigationConfig | undefined {
  const navigation = useStudioConfig()?.contextualNavigation
  const pathname = useStudioPathname()
  const matchPath = useStudioMatchPath()

  return navigation?.find(context => matchPath({ path: context.path, end: false }, pathname))
}

export function ContextualNavigationPanel({
  expanded,
  navigation,
  onBack,
  onNavigate,
}: {
  expanded: boolean
  navigation: ContextualNavigationConfig
  onBack: () => void
  onNavigate?: () => void
}) {
  const t = useStudioTranslate()

  return (
    <nav aria-label={navigation.label} className="flex w-full flex-col">
      <div className="pb-3 border-b border-b-gray-800">
        <SidebarAction
          expanded={expanded}
          icon={<ChevronLeftIcon />}
          label={t('studio.common.back')}
          onClick={onBack}
        />
      </div>
      {navigation.sections.map(section => (
        <div key={section.id} className="flex flex-col gap-1 border-b border-b-gray-800 py-3">
          {section.items.map(item => (
            <ContextualNavigationRow
              key={item.id}
              expanded={expanded}
              item={item}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </nav>
  )
}

function ContextualNavigationRow({
  expanded,
  item,
  onNavigate,
}: {
  expanded: boolean
  item: ContextualNavigationItem
  onNavigate?: () => void
}) {
  if (!item.route) {
    return (
      <Tooltip
        value={item.label}
        side="right"
        sideOffset={8}
        delay={0}
        open={expanded ? false : undefined}
      >
        <div>
          <MenuRow
            aria-disabled="true"
            className="cursor-default opacity-50 hover:bg-transparent"
            disabled
            variant="slim"
          >
            <MenuRow.Icon>{item.icon}</MenuRow.Icon>
            {expanded ? <MenuRow.Label>{item.label}</MenuRow.Label> : null}
          </MenuRow>
        </div>
      </Tooltip>
    )
  }

  return (
    <NavigationItem
      exact
      expanded={expanded}
      icon={item.icon}
      onNavigate={onNavigate}
      route={item.route}
      title={item.label}
    />
  )
}

function SidebarAction({
  expanded,
  icon,
  label,
  onClick,
}: {
  expanded: boolean
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <Tooltip
      value={label}
      side="right"
      sideOffset={8}
      delay={0}
      open={expanded ? false : undefined}
    >
      <div>
        <MenuRow variant="slim" className={tw('cursor-pointer')} onClick={onClick}>
          <MenuRow.Icon>{icon}</MenuRow.Icon>
          {expanded ? <MenuRow.Label>{label}</MenuRow.Label> : null}
        </MenuRow>
      </div>
    </Tooltip>
  )
}
